import Autocomplete from '@components/v2/form/Autocomplete/Autocomplete';
import { sortBy } from 'lodash';
import * as R from 'ramda';
import { useEffect, useRef, useState } from 'react';
import { useListPick } from '../../../../../../components/ListPicker/useListPick';
import LangEnum from '../../../../../../lang/LangEnum';
import useOrderBookStore from '../../../../../../stores/OrderBook/orderBook.store';
import { getFormattedCodeName } from '../../../../../../utils/formatters';
import { onScrollEndReload } from '../../../../../../utils/generics';
import { useApplicationContext } from '../../../../../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../../../../translation/TranslationContext';

export type CatalogPriceTable = {
  id?: string;
  name?: string;
  code?: string;
  defaultTable?: boolean;
  priceTableId?: string;
  defaultPriceTableSalesRepresentative?: boolean;
};

type PriceTableAutoCompleteProps = {
  handleChange: (value: string | null) => void;
  errorHelperText: string;
  priceTableIdInitial?: string;
  disabled: boolean;
};

const PriceTableAutoComplete = ({
  handleChange,
  errorHelperText,
  priceTableIdInitial,
  disabled,
}: PriceTableAutoCompleteProps) => {
  const { translate } = useTranslation();
  const { isAdminBrand, brandId } = useApplicationContext();
  const ref = useRef(null);
  const [inputText, setInputText] = useState('');
  const [selectedPriceTable, setSelectedPriceTable] = useState<CatalogPriceTable | null>(null);
  const selectedOrder = useOrderBookStore((orderBookState) => orderBookState.selectedOrder);

  const url = isAdminBrand
    ? 'price-tables'
    : `sales-representatives/export-catalog/available-price-tables`;

  const listPickActions = useListPick<Partial<CatalogPriceTable>>({
    url,
    queryField: 'nameOrCode',

    limit: 30,
    defaultQuery: {
      brandId,
      active: true,
      orderId: isAdminBrand ? undefined : selectedOrder?.id,
      customerId: isAdminBrand ? undefined : selectedOrder?.customer?.id,
    },
    shouldFetch: true,
    order: {
      code: 'ASC',
      defaultTable: 'ASC',
    },
  });

  const sortOptionsByCodeAndName = (data: Partial<CatalogPriceTable[]>): CatalogPriceTable[] => {
    const optionsWithCodes = data.filter((option) => !!option?.code);
    const optionsWithOnlyNames = data.filter((option) => !option?.code);

    const sortCodes = sortBy(optionsWithCodes, [
      function code(o) {
        if (Number.isFinite(Number(o?.code))) {
          return Number(o?.code);
        }
        return o?.code?.toUpperCase();
      },
    ]);
    const sortNames = sortBy(optionsWithOnlyNames, [
      function name(o) {
        return o?.name?.toUpperCase();
      },
    ]);

    return [...sortCodes, ...sortNames].filter((item): item is CatalogPriceTable => !!item);
  };

  const formatOptions = (data: Partial<CatalogPriceTable>[]) =>
    sortOptionsByCodeAndName(
      data.map((priceTable) => ({
        ...priceTable,
        id: priceTable.priceTableId ?? priceTable.id,
      })),
    );

  const getOptions = () =>
    R.uniqBy(
      ({ name }) => ({
        name,
      }),
      selectedPriceTable
        ? [selectedPriceTable, ...formatOptions(listPickActions.leftItems)]
        : formatOptions(listPickActions.leftItems),
    );

  const options = getOptions();
  useEffect(() => {
    const priceTableInitial = options.find((option) => option.id === priceTableIdInitial);
    const defaultPriceTableSalesRepresentative = options.find(
      (option) => option.defaultPriceTableSalesRepresentative,
    );
    const defaultPriceTableBrand = options.find((option) => option.defaultTable);

    const defaultPriceTable =
      priceTableInitial || defaultPriceTableSalesRepresentative || defaultPriceTableBrand;

    if (!selectedPriceTable && defaultPriceTable?.id) {
      setSelectedPriceTable(defaultPriceTable);
      handleChange(defaultPriceTable.id);
    }
  }, [options]);

  const handleChangeInput = (_: React.ChangeEvent<{}>, value: string) => {
    setInputText(value);
    if (disabled) {
      return;
    }
    listPickActions.onSearch(value);
  };

  return (
    <Autocomplete<CatalogPriceTable>
      ref={ref}
      clearOnBlur
      disabled={disabled}
      options={options}
      value={selectedPriceTable}
      inputValue={inputText}
      loading={listPickActions.loading}
      onInputChange={handleChangeInput}
      noOptionsText={translate(LangEnum.GLOBAL_NO_OPTIONS)}
      loadingText={translate(LangEnum.GLOBAL_SEARCHING)}
      getOptionLabel={(option) => getFormattedCodeName(option.name, option.code)}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      testId="catalog-export-modal-price-table-id"
      textFieldProps={{
        required: true,
        label: translate(LangEnum.CATALOGS_EXPORT_PRICE_TABLE_LABEL),
        error: !!errorHelperText,
        helperText: errorHelperText,
      }}
      slotProps={{
        listbox: {
          style: { maxHeight: 248 },
          onScroll: (event: React.SyntheticEvent) => onScrollEndReload(event, listPickActions),
        },
      }}
      onChange={(_, priceTable) => {
        if (priceTable?.id) {
          handleChange(priceTable.id);
          setSelectedPriceTable(priceTable);
        }
      }}
    />
  );
};

export default PriceTableAutoComplete;
